import { useState } from 'react';
import styled from 'styled-components';
import Icon from 'components/Icon';
import { ACCENTS, COLORS, FONT, focusRing } from '../lib/tokens';

const Card = styled.article`
  background: ${COLORS.card};
  border: 1px solid ${({ $a }) => $a.border};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  font-family: ${FONT};
  ${({ $a }) => ($a.shadow ? `box-shadow: ${$a.shadow};` : '')}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconTile = styled.span`
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ $a }) => $a.tint};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Name = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 600;
  color: ${COLORS.text};
`;

const Price = styled.span`
  flex: none;
  font-size: 18px;
  font-weight: 700;
  color: ${({ $a }) => $a.price};
  white-space: nowrap;
`;

const Lines = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Line = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.4;
  color: ${COLORS.muted};
`;

const CheckBg = styled.span`
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${({ $a }) => $a.tint};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  padding-top: 6px;
`;

const Option = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${({ $on, $a }) => ($on ? $a.color : COLORS.cardBorder)};
  background: ${({ $on, $a }) => ($on ? $a.tint : COLORS.tile)};
  color: ${COLORS.text};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  ${focusRing}
`;

const OptionLabel = styled.span`
  font-size: 12px;
  line-height: 1.2;
  color: ${COLORS.muted};
`;

const OptionPrice = styled.span`
  font-size: 16px;
  line-height: 1.2;
  font-weight: 700;
  color: ${({ $on, $a }) => ($on ? $a.price : COLORS.text)};
  white-space: nowrap;
`;

const BestValue = styled.span`
  position: absolute;
  top: -9px;
  right: 10px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ $a }) => $a.color};
  color: #0a0f2a;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.4;
`;

const Cta = styled.button`
  width: 100%;
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: ${COLORS.cta};
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s;

  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
  &:disabled {
    background: ${COLORS.cardBorder};
    color: ${COLORS.muted};
    cursor: default;
  }
  ${focusRing}
`;

const PlanCard = ({ card, onSelect }) => {
  const accent = ACCENTS[card.accent] || ACCENTS.grey;
  const [interval, setInterval] = useState(card.defaultInterval || 'monthly');
  const hasOptions = card.intervals.length > 0;
  return (
    <Card $a={accent} data-tier={card.tier} data-accent={card.accent}>
      <Header>
        <IconTile $a={accent} aria-hidden="true">
          <Icon type={card.icon} dimensions={16} col={accent.color} />
        </IconTile>
        <Name>{card.title}</Name>
        {!hasOptions && <Price $a={accent}>{card.priceText}</Price>}
      </Header>
      {card.lines.length > 0 && (
        <Lines>
          {card.lines.map((line, i) => (
            <Line key={`${i}-${line}`}>
              <CheckBg $a={accent} aria-hidden="true">
                <Icon type="paywallCheck" dimensions={11} col={accent.color} />
              </CheckBg>
              <span>{line}</span>
            </Line>
          ))}
        </Lines>
      )}
      {hasOptions && (
        <Options role="radiogroup" aria-label={card.title}>
          {card.intervals.map((option) => {
            const on = option.interval === interval;
            return (
              <Option
                key={option.interval}
                type="button"
                role="radio"
                aria-checked={on}
                $on={on}
                $a={accent}
                disabled={option.disabled}
                onClick={() => setInterval(option.interval)}
              >
                {card.bestValueInterval === option.interval && <BestValue $a={accent}>{card.bestValueLabel}</BestValue>}
                <OptionLabel>{option.label}</OptionLabel>
                <OptionPrice $on={on} $a={accent}>{option.priceText}</OptionPrice>
              </Option>
            );
          })}
        </Options>
      )}
      {card.ctaLabel && (
        <Cta type="button" disabled={card.ctaDisabled} onClick={() => onSelect(card, interval)}>
          {card.ctaLabel}
        </Cta>
      )}
    </Card>
  );
};

export default PlanCard;
